import type { AvailabilityResponse } from '@components/calendar/types';
import { CALENDAR_RANGE_YEARS, DATE_KEY_FORMAT } from '@constants/calendar';
import { gpBaseUrl } from '@settings';
import { addYears, format } from 'date-fns';

interface FetchAvailabilityParams {
	storefrontId: string;
	startDate?: Date;
	endDate?: Date;
	timeoutMs?: number;
}

export const AVAILABILITY_STATUS = {
	OK: 'OK',
	NOT_CONFIGURED: 'NOT_CONFIGURED',
	ERROR: 'ERROR',
} as const;

export interface AvailabilityFetchResponse {
	status: keyof typeof AVAILABILITY_STATUS;
	response: AvailabilityResponse | null;
}

export const TIMEOUT_ERROR_MESSAGE = 'Request timed out';

const availabilityStatus = (status: number) => {
	if (status === 204) return AVAILABILITY_STATUS.NOT_CONFIGURED;
	if (status !== 200) return AVAILABILITY_STATUS.ERROR;
	return AVAILABILITY_STATUS.OK;
};

export const fetchAvailability = async ({
	storefrontId,
	startDate = new Date(),
	endDate = addYears(new Date(), CALENDAR_RANGE_YEARS),
	timeoutMs = 5000,
}: FetchAvailabilityParams): Promise<AvailabilityFetchResponse> => {
	try {
		const options: RequestInit = {
			method: 'POST',
			body: JSON.stringify({
				storefrontId: storefrontId,
				dateRange: {
					start: format(startDate, DATE_KEY_FORMAT),
					end: format(endDate, DATE_KEY_FORMAT),
				},
			}),
			signal: AbortSignal.timeout(timeoutMs),
		};

		const response = await fetch(
			`${gpBaseUrl}/vendor/v1/availability/check`,
			options,
		);

		return {
			status: availabilityStatus(response.status),
			response: response.status === 200 ? await response.json() : null,
		};
	} catch (error) {
		if (error.name === 'AbortError' || error.name === 'TimeoutError') {
			throw new Error(TIMEOUT_ERROR_MESSAGE);
		}
		throw error;
	}
};
